import type { EventData } from "@/services/sanity/types";

export const siteConfig = {
    name: "Central Auckland Church of Christ",
    shortName: "Auckland Church",
    url: "https://aucklandchurch.com",
    defaultTitle: "Central Auckland Church of Christ",
    defaultDescription: "Join Central Auckland Church of Christ for Sunday worship in Freemans Bay, Auckland. Explore ministries, events, resources, and ways to connect with our church family.",
    defaultImagePath: "/logo.svg",
    locale: "en_NZ",
    aiDescription:
        "Central Auckland Church of Christ is a Christian church in Auckland, New Zealand, with Sunday worship, midweek groups, community events, giving information, and faith resources for people exploring Jesus and church life.",
    email: "darvodelsky@gmail.com",
    address: {
        streetAddress: "52 Hepburn Street",
        addressLocality: "Freemans Bay",
        addressRegion: "Auckland",
        postalCode: "1011",
        addressCountry: "NZ",
    },
    sameAs: ["https://www.facebook.com/pages/Auckland-Church-Central-Auckland-Church-of-Christ/409907289088953", "https://www.instagram.com/icocauckland/", "https://www.youtube.com/@AucklandChurch"],
} as const;

export type StructuredData = Record<string, unknown>;

export function buildCanonicalUrl(path = "/"): string {
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    return new URL(normalizedPath, siteConfig.url).toString();
}

export const organizationStructuredData: StructuredData = {
    "@context": "https://schema.org",
    "@type": "Church",
    name: siteConfig.name,
    url: siteConfig.url,
    description: siteConfig.defaultDescription,
    email: siteConfig.email,
    address: {
        "@type": "PostalAddress",
        ...siteConfig.address,
    },
    sameAs: siteConfig.sameAs,
};

export const websiteStructuredData: StructuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: siteConfig.name,
    alternateName: siteConfig.shortName,
    url: siteConfig.url,
    description: siteConfig.defaultDescription,
    inLanguage: "en-NZ",
};

export function buildWebPageStructuredData(title: string, description: string, path: string, inLanguage = "en-NZ"): StructuredData {
    return {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: title,
        description,
        url: buildCanonicalUrl(path),
        isPartOf: {
            "@type": "WebSite",
            name: siteConfig.name,
            url: siteConfig.url,
        },
        inLanguage,
    };
}

function computeEndDate(startDate: string): string {
    try {
        const end = new Date(new Date(startDate).getTime() + 2 * 60 * 60 * 1000);
        return end.toISOString();
    } catch {
        return startDate;
    }
}

export function buildEventStructuredData(events: EventData[]): StructuredData[] {
    return events
        .filter((event) => Boolean(event.title && event.date && event.location))
        .map((event) => ({
            "@context": "https://schema.org",
            "@type": "Event",
            name: event.title,
            description: event.description ?? `${event.title} at ${event.location}, hosted by ${siteConfig.name}.`,
            startDate: event.date,
            endDate: event.endDate ?? computeEndDate(event.date),
            image: event.image ?? `${siteConfig.url}/logo.svg`,
            eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
            eventStatus: event.isUpcoming ? "https://schema.org/EventScheduled" : "https://schema.org/EventCompleted",
            location: {
                "@type": "Place",
                name: event.location,
                address: {
                    "@type": "PostalAddress",
                    streetAddress: event.address,
                    addressLocality: "Auckland",
                    addressCountry: "NZ",
                },
            },
            organizer: {
                "@type": "Church",
                name: siteConfig.name,
                url: siteConfig.url,
            },
            performer: {
                "@type": "Organization",
                name: siteConfig.name,
                url: siteConfig.url,
            },
            offers: {
                "@type": "Offer",
                price: "0",
                priceCurrency: "NZD",
                availability: "https://schema.org/InStock",
                url: `${siteConfig.url}/visit`,
            },
        }));
}
